"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Headphones, ArrowLeft, RefreshCw, Layers } from "lucide-react";
import { useState } from "react";

interface TelecallingTopBarProps {
  currency?: "INR" | "USD";
  onCurrencyToggle?: (c: "INR" | "USD") => void;
  onResetDemo?: () => void;
  isResetting?: boolean;
}

export function TelecallingTopBar({
  currency = "INR",
  onCurrencyToggle,
  onResetDemo,
  isResetting = false,
}: TelecallingTopBarProps) {
  const pathname = usePathname();
  const isCrm = pathname?.includes("/crm");

  return (
    <header className="sticky top-0 z-50 w-full bg-slate-950/80 backdrop-blur-md border-b border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        {/* Left: Brand & Back to Hub */}
        <div className="flex items-center gap-4">
          <Link
            href="/demos"
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">All Demos</span>
          </Link>
          <div className="h-4 w-px bg-slate-800" />
          <Link href="/demos/telecalling" className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/20 group-hover:scale-105 transition-transform">
              <Headphones className="w-4.5 h-4.5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-white tracking-tight text-sm sm:text-base">Vocalis BPO</span>
                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400 font-semibold border border-cyan-500/20">
                  Outbound & Care
                </span>
              </div>
              <p className="text-[10px] text-slate-400 hidden sm:block">
                SOC-2 Type II • ISO 27001 • Telecalling Floor CRM
              </p>
            </div>
          </Link>
        </div>

        {/* Center / Navigation Switcher */}
        <div className="flex items-center bg-slate-900/90 border border-slate-800 p-1 rounded-xl">
          <Link
            href="/demos/telecalling"
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              !isCrm
                ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow"
                : "text-slate-400 hover:text-white"
            }`}
          >
            Client Portal
          </Link>
          <Link
            href="/demos/telecalling/crm"
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all ${
              isCrm
                ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow"
                : "text-slate-400 hover:text-white"
            }`}
          >
            <Layers className="w-3.5 h-3.5" />
            Calling Floor CRM
          </Link>
        </div>

        {/* Right: Currency & Reset */}
        <div className="flex items-center gap-2 sm:gap-3">
          {onCurrencyToggle && (
            <div className="flex items-center bg-slate-900 border border-slate-800 rounded-lg p-0.5 text-xs">
              <button
                type="button"
                onClick={() => onCurrencyToggle("INR")}
                className={`px-2 py-1 rounded font-semibold transition-colors ${
                  currency === "INR" ? "bg-cyan-600 text-white" : "text-slate-400 hover:text-slate-200"
                }`}
              >
                ₹ INR
              </button>
              <button
                type="button"
                onClick={() => onCurrencyToggle("USD")}
                className={`px-2 py-1 rounded font-semibold transition-colors ${
                  currency === "USD" ? "bg-cyan-600 text-white" : "text-slate-400 hover:text-slate-200"
                }`}
              >
                $ USD
              </button>
            </div>
          )}

          {onResetDemo && (
            <button
              type="button"
              onClick={onResetDemo}
              disabled={isResetting}
              className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-cyan-400 transition-colors px-2 py-1 rounded-lg border border-slate-800 hover:border-cyan-500/30 bg-slate-900/60"
              title="Reset Demo Data to Initial Seed"
            >
              <RefreshCw className={`w-3 h-3 ${isResetting ? "animate-spin text-cyan-400" : ""}`} />
              <span className="hidden md:inline">Reset Demo</span>
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
